import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  declaredScrollMargin,
  scrollTargetIntoRoot,
  useAppScrollRoot,
} from '~/components/Layout/appScrollRoot';
import { anchorClearance } from '~/routing/ScrollToTop';
import { FAQ_LAYOUT_CLASSES, type FaqLayout } from './faqLayout';
import type { FaqNavGroup, FaqSection } from './faqTypes';

const noop = () => undefined;

// Highlights the section currently in view as the user scrolls.
//
// [MCV-1724] The activation line is `anchorClearance` -- the very offset
// `ScrollToTop` and `scrollIntoView` park a jumped-to section at -- so the
// section a jump lands on is always the active one. This used to be an
// IntersectionObserver band starting at a fixed 96px, which is above the 160px
// mobile clearance (80px header plus the sticky "Jump to" bar): after a jump the
// previous section's tail was still inside the band and won, so the nav and the
// toggle named the wrong section. Desktop only worked because its 112px
// clearance happens to put that tail above 96px.
//
// It recomputes on scroll, on resize, on any change to a section's height and on
// demand: `refresh` is for an action that can move the highlight without moving
// the page or the layout. Clicking the last nav item while already scrolled to the
// bottom is one -- `scrollIntoView` has nowhere to go and `replaceState` fires no
// event -- so `NavTree` calls `refresh` once it has written the hash.
//
// [MCV-1724] `scrollRoot` is for the signed-in app, where the document never
// scrolls: only Layout's main column does. With it, the spy listens to that
// element's scroll events (they do not bubble to window) and measures the
// activation line and the bottom of the page against that element. Without it
// (the public site, `null`) everything below is the window-based behaviour the
// public page has always had.
function useActiveSection(
  ids: string[],
  scrollRoot: RefObject<HTMLElement | null> | null
): { active: string; refresh: () => void } {
  const [active, setActive] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      return window.location.hash.slice(1);
    }
    return ids[0] ?? '';
  });

  // `schedule` lives inside the effect (it closes over `ids`), so `refresh` goes
  // through a ref to stay one stable function across renders and effect re-runs.
  const scheduleRef = useRef<() => void>(noop);
  const refresh = useCallback(() => scheduleRef.current(), []);

  const key = ids.join('|');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const root = scrollRoot?.current ?? null;
    const scroller: HTMLElement | Window = root ?? window;

    let frame = 0;

    const recompute = () => {
      frame = 0;

      const sections: Array<{ id: string; el: HTMLElement }> = [];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) sections.push({ id, el });
      }
      if (sections.length === 0) return;

      const rects = sections.map(({ id, el }) => ({ id, rect: el.getBoundingClientRect() }));

      // No layout at all (jsdom, or the page is hidden): every rect is empty, so
      // there is nothing to measure. Keep whatever the hash seeded.
      if (rects.every(({ rect }) => rect.height === 0)) return;

      // +1px absorbs the sub-pixel rounding of `scrollY` -- a jump lands a
      // section at the clearance to within a fraction of a pixel, and that
      // fraction must not tip it onto the wrong side of the line.
      //
      // In the app the line is the scroller's own top plus the section's declared
      // scroll margin, which is where `scrollIntoView` parks it there.
      // `anchorClearance` is not used: it floors at the document's `<header>`
      // height, a fixed site header the app shell does not have.
      const line = root
        ? root.getBoundingClientRect().top + declaredScrollMargin(sections[0].el) + 1
        : anchorClearance(sections[0].el) + 1;

      // The section with the greatest top at or above the line, not the first
      // one found, so the answer does not depend on document order.
      let best: string | null = null;
      let bestTop = Number.NEGATIVE_INFINITY;
      for (const { id, rect } of rects) {
        if (rect.top <= line && rect.top > bestTop) {
          bestTop = rect.top;
          best = id;
        }
      }

      // The page can run out of road before the last section reaches the line
      // (a short final section leaves its top well below it, even fully
      // scrolled), and the rule above would then name the section before it, so
      // clicking the last item would highlight its neighbour. `NavTree` writes
      // the clicked id into the hash, so at the bottom of the document a
      // section the hash names that is on screen but has not reached the line
      // wins -- it can only ever be promoted past what the line chose, never
      // demoted behind it.
      const atBottom = root
        ? root.scrollTop + root.clientHeight >= root.scrollHeight - 1
        : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
      if (atBottom) {
        const hashId = window.location.hash.slice(1);
        const named = rects.find(({ id }) => id === hashId);
        const viewBottom = root ? root.getBoundingClientRect().bottom : window.innerHeight;
        if (named && named.rect.top > line && named.rect.top < viewBottom) {
          setActive(named.id);
          return;
        }
      }

      // Nothing has reached the line yet: the top of the page, above the first
      // section.
      setActive(best ?? ids[0] ?? '');
    };

    // One recompute per frame however many scroll events arrive.
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(recompute);
    };

    scheduleRef.current = schedule;

    scroller.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    // [MCV-1724] A section can cross the line with no scroll event: FAQ answers are
    // disclosures, so opening or closing one changes the heights above the sections
    // after it. Safari has no scroll anchoring to absorb that and Chrome's does not
    // always, and the IntersectionObserver this replaced re-fired on any such move.
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => schedule());
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) resizeObserver.observe(el);
      }
    }

    // Also once on mount, a frame later so it runs after `ScrollToTop`'s effect
    // has moved a cold-loaded `/faq#section` to its offset.
    schedule();

    return () => {
      scheduleRef.current = noop;
      scroller.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      resizeObserver?.disconnect();
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [key, scrollRoot]);

  return { active, refresh };
}

const linkBase = 'block rounded-r-md border-l py-1.5 pl-3 pr-2 text-sm leading-6 transition-colors';

type NavTreeProps = {
  activeId: string;
  navGroups: FaqNavGroup[];
  sectionById: Record<string, FaqSection>;
  /** Moves the page so the clicked section is at its landing offset. */
  scrollToSection: (el: HTMLElement) => void;
  onNavigate?: (id: string) => void;
  /** Called once a click has moved the page and written the hash, so the spy can re-read both. */
  onJump?: () => void;
};

function NavTree({
  activeId,
  navGroups,
  sectionById,
  scrollToSection,
  onNavigate,
  onJump,
}: NavTreeProps) {
  return (
    <nav aria-label="FAQ sections">
      <ul className="space-y-6">
        {navGroups.map((group) => (
          <li key={group.id}>
            {/* [MCV-1592] Title only. `group.description` is prose -- some HLX
                blurbs run a full paragraph -- and rendering it here buried the
                links it was meant to introduce. It now sits above its sections
                in the main column, where it reads as content rather than as
                navigation. */}
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-grey-400">
              {group.title}
            </p>
            <ul className="mt-2 space-y-0.5 border-l border-grey-800">
              {group.sectionIds.map((id) => {
                const section = sectionById[id];
                if (!section) return null;
                const isActive = activeId === id;
                return (
                  <li key={id} className="-ml-px">
                    <a
                      href={`#${id}`}
                      onClick={(event) => {
                        const el = document.getElementById(id);
                        if (el) {
                          event.preventDefault();
                          // Jump straight to the section (no animated full-page scroll);
                          // scroll-margin-top on the section keeps it clear of the header.
                          scrollToSection(el);
                          // replaceState (not pushState) so clicking through sections
                          // doesn't stack history entries the user must Back out of.
                          // [MCV-1724] Keep the current history state: in the signed-in app
                          // react-router stores its `{ usr, key, idx }` there, and nulling it
                          // would lose the entry's key and index.
                          window.history.replaceState(window.history.state, '', `#${id}`);
                          // [MCV-1724] At the bottom of the page the jump above moves
                          // nothing and the replaceState fires no event, so without
                          // this the spy never sees the new hash.
                          onJump?.();
                        }
                        onNavigate?.(id);
                      }}
                      aria-current={isActive ? 'true' : undefined}
                      data-cy={`faq-nav-${id}`}
                      className={[
                        linkBase,
                        isActive
                          ? 'border-primary-50 bg-primary-50 font-semibold text-white'
                          : 'border-transparent text-grey-300 hover:border-grey-600 hover:text-grey-100',
                      ].join(' ')}
                    >
                      {section.title}
                    </a>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

type FaqSideNavProps = {
  navGroups: FaqNavGroup[];
  sectionById: Record<string, FaqSection>;
  sectionOrder: string[];
  /** [MCV-1724] `app` follows Layout's scrolling column; `public` (the default) follows the window. */
  layout?: FaqLayout;
};

// Left-hand navigation tree on desktop; collapses to a sticky dropdown on
// mobile. Content arrives as props rather than being imported directly, so the
// brand-split knowledge base can render either section set. [MCV-1501]
export const FaqSideNav = ({
  navGroups,
  sectionById,
  sectionOrder,
  layout = 'public',
}: FaqSideNavProps) => {
  const classes = FAQ_LAYOUT_CLASSES[layout];
  const appScrollRoot = useAppScrollRoot();
  const scrollRoot = layout === 'app' ? appScrollRoot : null;
  const { active: activeId, refresh } = useActiveSection(sectionOrder, scrollRoot);
  const activeSection = sectionById[activeId];

  // [MCV-1724] In the app only Layout's column moves. `scrollIntoView` would scroll the document
  // too. The public page keeps `scrollIntoView`: there the document is the scroller.
  const scrollToSection = useCallback(
    (el: HTMLElement) => {
      const root = scrollRoot?.current;
      if (root) scrollTargetIntoRoot(root, el);
      else el.scrollIntoView({ block: 'start' });
    },
    [scrollRoot]
  );

  return (
    <>
      {/* Mobile: sticky dropdown, pinned directly below the fixed PublicHeader
          rather than at `top-0` — the header is opaque and `z-50`, so a nav
          stuck at 0 would sit underneath it and be unclickable. [MCV-1514]
          [MCV-1724] In the app there is no header over the column, so it
          sticks at the column's own top. */}
      <div className={classes.mobileNav}>
        <Popover className="relative">
          <PopoverButton
            data-cy="faq-nav-mobile-toggle"
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-grey-800 bg-white px-4 py-2.5 text-left text-sm font-semibold text-grey-100 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-50"
          >
            <span className="truncate">
              <span className="text-grey-400">Jump to: </span>
              {activeSection ? activeSection.title : 'Select a section'}
            </span>
            <ChevronDownIcon aria-hidden="true" className="size-5 shrink-0 text-grey-400" />
          </PopoverButton>
          <PopoverPanel className="absolute inset-x-0 top-full z-30 mt-2 max-h-[70vh] overflow-y-auto rounded-lg border border-grey-800 bg-white p-4 shadow-lg">
            {({ close }) => (
              <NavTree
                activeId={activeId}
                navGroups={navGroups}
                sectionById={sectionById}
                scrollToSection={scrollToSection}
                onNavigate={() => close()}
                onJump={refresh}
              />
            )}
          </PopoverPanel>
        </Popover>
      </div>

      {/* Desktop: sticky sidebar tree (direct child of the tall grid cell so sticky
          has room to travel down the full page) */}
      <div className={classes.desktopNav}>
        <NavTree
          activeId={activeId}
          navGroups={navGroups}
          sectionById={sectionById}
          scrollToSection={scrollToSection}
          onJump={refresh}
        />
      </div>
    </>
  );
};
